// Per-TAB signal: did THIS tab just send a CRM-record-mutating request?
//
// Used to suppress the yellow "someone changed this" flash for the user's OWN
// edits on the tab where they made them. A different tab — even the SAME user
// in another window — never calls markSelfMutation(), so it still flashes.
// This is what distinguishes "I edited this here" from "someone (maybe me,
// elsewhere) edited this", which actor_user_id alone cannot (same user).
//
// Window (8s): a mutation's own record_change echo comes back ~1-2s later
// (notifier poll + WS + debounce + refetch), or a few seconds under backend
// load; a race between the optimistic local patch and that refetch can flag the
// user's own change as new. Suppressing flashes briefly after a self-edit
// closes that race centrally, without threading state through every page.
//
// Trade-off (accepted): a DIFFERENT user's change arriving within the window on
// the same tab won't flash — its value still updates via refetch. Per-record
// scoping was considered but rejected: line items are shared and composite
// edits (PATCH /crm/quotes/{h}/composite-update) change line items WITHOUT
// naming their ids in the URL, so id-scoped suppression would leak and
// re-flash the user's own composite edits. A short global window is robust.

const SELF_MUTATION_WINDOW_MS = 8000;

let lastSelfMutationAt = 0;

export const markSelfMutation = () => {
  lastSelfMutationAt = Date.now();
};

export const wasSelfMutationRecent = (windowMs = SELF_MUTATION_WINDOW_MS) =>
  Date.now() - lastSelfMutationAt < windowMs;

// A record_change from ANOTHER user, seen recently on this tab. The self-
// mutation gate must NOT swallow a colleague's change just because THIS tab
// also edited something in the last few seconds — otherwise the colleague's
// comment/price renders (the refetch is unconditional) but never flashes, and
// worse, once it's folded into the local baseline it can never flash again.
// So suppression applies only when we self-mutated AND no foreign change is in
// flight. Trade-off: if a foreign change coincides with our own edit, our own
// edit may also flash (a minor extra ring) — strictly better than missing a
// colleague's change. Marked by the app-wide record_change listener, which
// alone knows the current user id to tell "foreign" from "mine".
let lastForeignChangeAt = 0;

export const markForeignChange = () => {
  lastForeignChangeAt = Date.now();
};

export const wasForeignChangeRecent = (windowMs = SELF_MUTATION_WINDOW_MS) =>
  Date.now() - lastForeignChangeAt < windowMs;

// URLs whose successful mutation broadcasts a record_change the UI can flash.
// Scoped to the broadcast entity types (quote/sale/return/line_item + the
// shipment endpoints that write sale/return activity) so mutating an unrelated
// resource (contact, company, address, task) does NOT arm the suppression.
export const isSelfFlashableMutationUrl = (url) =>
  typeof url === 'string' && /\/crm\/(quotes|sales|returns|line-items|shipments)(\/|\?|$)/.test(url);
